import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie } from "@phosphor-icons/react";
import { getConsent, setConsent } from "@/lib/consent";
import { initMetaPixel } from "@/lib/meta-pixel";
import { useAuth } from "@/context/AuthContext";

// Publika routes där GDPR-bannern får visas. Inloggade sidor slipper —
// användare som skapat konto har redan accepterat användarvillkor.
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/pricing",
  "/integrationer",
  "/accept-invite",
  "/billing/success",
  "/billing/cancel",
];

function isPublicPath(pathname) {
  return PUBLIC_PATHS.includes(pathname);
}

export default function CookieBanner() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [consent, setConsentState] = useState(() => getConsent());

  // Om samtycke redan finns vid mount OCH är "accepted", initiera pixeln
  // direkt så att inloggade besökare som redan tackat ja får PageView-events.
  useEffect(() => {
    if (getConsent() === "accepted") {
      initMetaPixel();
    }
    const onChange = () => setConsentState(getConsent());
    window.addEventListener("cookie-consent-changed", onChange);
    return () => window.removeEventListener("cookie-consent-changed", onChange);
  }, []);

  const accept = () => {
    setConsent("accepted");
    initMetaPixel();
    setConsentState("accepted");
  };

  const reject = () => {
    setConsent("rejected");
    setConsentState("rejected");
  };

  // Visa endast om (a) inget val gjorts än, (b) besökaren INTE är inloggad,
  // och (c) vi är på en publik sida. Inloggade slipper — de har redan
  // godkänt användarvillkor vid registreringen.
  if (consent) return null;
  if (loading) return null;
  if (user) return null;
  if (!isPublicPath(location.pathname)) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 inset-x-0 z-[60] px-4 pb-4 pointer-events-none"
    >
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie-samtycke"
        className="pointer-events-auto mx-auto max-w-3xl bg-white border border-app rounded-2xl shadow-xl p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-start"
      >
        <div className="flex-shrink-0 hidden sm:block">
          <div className="w-10 h-10 rounded-full bg-[#fff4ef] flex items-center justify-center">
            <Cookie size={22} className="text-[var(--primary,#c65a3b)]" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-base sm:text-lg text-text-primary">
            Vi använder cookies
          </h2>
          <p className="text-sm text-text-muted mt-1">
            Vi använder nödvändiga cookies för att appen ska fungera. Med ditt
            godkännande använder vi även analys- och marknadsföringscookies
            (Meta Pixel) för att förbättra tjänsten och mäta effekten av vår
            marknadsföring. Du kan när som helst ändra ditt val genom att
            rensa webbläsarens lagring.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              data-testid="cookie-accept-button"
              onClick={accept}
              className="tk-btn-primary sm:order-2"
            >
              Acceptera alla
            </button>
            <button
              data-testid="cookie-reject-button"
              onClick={reject}
              className="tk-btn-outline sm:order-1"
            >
              Endast nödvändiga
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
